/**
 * Modal para seleccionar items de presupuesto de venta
 * para asignar a un nuevo presupuesto de costos
 */
import React, { useState, useEffect } from 'react';
import {
  itemPresupuestoVentaService,
  itemPresupuestoCostoService,
  presupuestoCostoService
} from '../services/onebaseService';
import { toast } from 'react-toastify';
import '../styles/onebase-presupuestos.css';

import Loading from './Loading';
const SeleccionarItemsPresupuestoModal = ({ isOpen, onClose, presupuestoVenta, onItemsSelected }) => {
  const [items, setItems] = useState([]);
  const [itemsSeleccionados, setItemsSeleccionados] = useState({}); // Cambiado a objeto: { itemId: cantidad }
  const [cantidadesAsignadas, setCantidadesAsignadas] = useState({}); // { itemId: cantidadYaAsignada }
  const [loading, setLoading] = useState(false);
  const [loadingAsignadas, setLoadingAsignadas] = useState(false);

  useEffect(() => {
    if (isOpen && presupuestoVenta) {
      cargarItems();
      cargarCantidadesAsignadas();
    }
  }, [isOpen, presupuestoVenta]);

  const cargarItems = async () => {
    setLoading(true);
    try {
      const response = await itemPresupuestoVentaService.getAll({
        presupuesto_venta: presupuestoVenta.id,
        status: 'activo',
        limit: 1000
      });
      const itemsData = Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);
      setItems(itemsData);
    } catch (error) {
      console.error('Error al cargar items:', error);
      toast.error('Error al cargar items del presupuesto');
    } finally {
      setLoading(false);
    }
  };

  const cargarCantidadesAsignadas = async () => {
    setLoadingAsignadas(true);
    try {
      // 1. Obtener todos los presupuestos de costos del mismo proyecto
      const presupuestosCostoResponse = await presupuestoCostoService.getAll({
        proyecto: presupuestoVenta.proyecto,
        status: 'activo',
        limit: 1000
      });
      const presupuestosCosto = Array.isArray(presupuestosCostoResponse.data)
        ? presupuestosCostoResponse.data
        : (presupuestosCostoResponse.data.results || []);

      // 2. Obtener todos los items de esos presupuestos de costos
      const cantidades = {};
      for (const presupuestoCosto of presupuestosCosto) {
        const itemsResponse = await itemPresupuestoCostoService.getAll({
          presupuesto_costo: presupuestoCosto.id,
          status: 'activo',
          limit: 1000
        });
        const itemsCosto = Array.isArray(itemsResponse.data)
          ? itemsResponse.data
          : (itemsResponse.data.results || []);

        // 3. Sumar cantidades por descripción (ya que los items de costo se relacionan por descripción)
        itemsCosto.forEach(itemCosto => {
          const descripcion = itemCosto.descripcion;
          if (!cantidades[descripcion]) {
            cantidades[descripcion] = 0;
          }
          cantidades[descripcion] += parseFloat(itemCosto.cantidad) || 0;
        });
      }

      // 4. Mapear las cantidades asignadas por ID de item de venta
      const cantidadesPorId = {};
      items.forEach(item => {
        cantidadesPorId[item.id] = cantidades[item.descripcion] || 0;
      });

      setCantidadesAsignadas(cantidadesPorId);
    } catch (error) {
      console.error('Error al cargar cantidades asignadas:', error);
      toast.error('Error al calcular cantidades disponibles');
    } finally {
      setLoadingAsignadas(false);
    }
  };

  const getCantidadDisponible = (item) => {
    const cantidadTotal = parseFloat(item.cantidad) || 0;
    const cantidadAsignada = cantidadesAsignadas[item.id] || 0;
    return Math.max(0, cantidadTotal - cantidadAsignada);
  };

  const handleToggleItem = (item) => {
    setItemsSeleccionados(prev => {
      const newState = { ...prev };
      if (newState[item.id]) {
        delete newState[item.id];
      } else {
        // Al seleccionar, usar la cantidad disponible como default
        const cantidadDisponible = getCantidadDisponible(item);
        newState[item.id] = cantidadDisponible > 0 ? cantidadDisponible : 0;
      }
      return newState;
    });
  };

  const handleCantidadChange = (itemId, valor) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const cantidadDisponible = getCantidadDisponible(item);
    let cantidad = parseFloat(valor) || 0;

    // Validar que no exceda la cantidad disponible
    if (cantidad > cantidadDisponible) {
      cantidad = cantidadDisponible;
      toast.warning(`La cantidad no puede exceder ${cantidadDisponible}`);
    }

    if (cantidad < 0) {
      cantidad = 0;
    }

    setItemsSeleccionados(prev => ({
      ...prev,
      [itemId]: cantidad
    }));
  };

  const handleAsignarPresupuesto = () => {
    const itemsConCantidad = Object.entries(itemsSeleccionados)
      .filter(([_, cantidad]) => cantidad > 0)
      .map(([itemId, cantidad]) => {
        const item = items.find(i => i.id === parseInt(itemId));
        return {
          ...item,
          cantidad_seleccionada: cantidad
        };
      });

    if (itemsConCantidad.length === 0) {
      toast.error('Debes seleccionar al menos un item con cantidad mayor a 0');
      return;
    }

    onItemsSelected(itemsConCantidad);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(value || 0);
  };

  const getTotalItemsSeleccionados = () => {
    return Object.values(itemsSeleccionados).filter(cantidad => cantidad > 0).length;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content modal-lg"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '1400px' }}
      >
        <div className="modal-header">
          <div>
            <h2>Seleccionar Items para Presupuesto de Costos</h2>
            <p style={{ margin: '0.5rem 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
              Proyecto: {presupuestoVenta?.proyecto_nombre} - Cliente: {presupuestoVenta?.cliente_nombre}
            </p>
          </div>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body" style={{ padding: '1.5rem' }}>
          {loading || loadingAsignadas ? (
            <Loading fullScreen={false} size="sm" mensaje="Cargando items" />
          ) : items.length > 0 ? (
            <>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
                padding: '0.75rem',
                background: '#f9fafb',
                borderRadius: '0.5rem'
              }}>
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                  Total de items: {items.length}
                </span>
                <span style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: getTotalItemsSeleccionados() > 0 ? '#10b981' : '#6b7280'
                }}>
                  {getTotalItemsSeleccionados()} items seleccionados
                </span>
              </div>

              <div className="items-table" style={{ maxHeight: '450px', overflow: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}></th>
                      <th>Descripción</th>
                      <th>Presupuestado</th>
                      <th>Ya Asignado</th>
                      <th>Disponible</th>
                      <th>Cantidad a Asignar</th>
                      <th>Días</th>
                      <th>Unidad</th>
                      <th>Costo Unit.</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const cantidadDisponible = getCantidadDisponible(item);
                      const isSelected = itemsSeleccionados[item.id] !== undefined;
                      const cantidadSeleccionada = itemsSeleccionados[item.id] || 0;

                      return (
                        <tr
                          key={item.id}
                          className={isSelected && cantidadSeleccionada > 0 ? 'item-selected' : ''}
                          style={{
                            opacity: cantidadDisponible === 0 ? 0.5 : 1,
                            background: cantidadDisponible === 0 ? '#f9fafb' : 'transparent'
                          }}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleItem(item)}
                              disabled={cantidadDisponible === 0}
                              style={{ width: '18px', height: '18px', cursor: cantidadDisponible > 0 ? 'pointer' : 'not-allowed' }}
                            />
                          </td>
                          <td>{item.descripcion}</td>
                          <td style={{ fontWeight: '600' }}>{item.cantidad}</td>
                          <td style={{ color: '#f59e0b', fontWeight: '500' }}>
                            {cantidadesAsignadas[item.id] || 0}
                          </td>
                          <td style={{
                            fontWeight: '700',
                            color: cantidadDisponible > 0 ? '#10b981' : '#ef4444'
                          }}>
                            {cantidadDisponible.toFixed(2)}
                          </td>
                          <td style={{ padding: '0.5rem' }}>
                            {isSelected ? (
                              <input
                                type="number"
                                value={cantidadSeleccionada}
                                onChange={(e) => handleCantidadChange(item.id, e.target.value)}
                                min="0"
                                max={cantidadDisponible}
                                step="0.01"
                                style={{
                                  width: '100px',
                                  padding: '0.25rem 0.5rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '0.25rem',
                                  fontSize: '0.875rem'
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <span style={{ color: '#9ca3af' }}>-</span>
                            )}
                          </td>
                          <td>{item.cantidad_dias || 1}</td>
                          <td>{item.unidad}</td>
                          <td>{formatCurrency(item.costo_unitario)}</td>
                          <td style={{ fontWeight: '600' }}>
                            {isSelected && cantidadSeleccionada > 0
                              ? formatCurrency(cantidadSeleccionada * (item.cantidad_dias || 1) * (item.costo_unitario || 0))
                              : formatCurrency(item.subtotal)
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <p>No hay items en este presupuesto de venta</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleAsignarPresupuesto}
            disabled={getTotalItemsSeleccionados() === 0}
            style={{
              opacity: getTotalItemsSeleccionados() === 0 ? 0.5 : 1,
              cursor: getTotalItemsSeleccionados() === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            Asignar Presupuesto ({getTotalItemsSeleccionados()})
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeleccionarItemsPresupuestoModal;
